import React, { useState, useEffect } from 'react';
import { User, ArrowRight, Shield, Building2, Award, Users, GraduationCap } from 'lucide-react';
import { Button } from './ui/button';
import { EditableText } from './inline/EditableText';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const tierIcons = {
  1: Shield,
  2: Building2,
  3: Award,
  4: Users,
  5: GraduationCap,
};

const tierLabels = {
  1: 'Government',
  2: 'Congressional',
  3: 'Industry',
  4: 'International',
  5: 'Academic',
};

const SpeakerPreviewSection = ({ content, sectionId }) => {
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);

  const data = {
    title: 'Featured Speakers',
    subtitle: 'Leaders shaping the nuclear future',
    max_speakers: 6,
    show_tiers: 'all',
    cta: { label: 'View All Speakers', url: '/speakers', style: 'primary' },
    ...content,
  };
  const scope = (path) => ({ type: 'homepage_section', sectionId, path });

  useEffect(() => {
    const fetchSpeakers = async () => {
      try {
        const res = await fetch(`${API_URL}/api/speakers?visible_only=true`);
        if (res.ok) {
          let list = await res.json();
          if (data.show_tiers && data.show_tiers !== 'all') {
            const tierMap = { keynote: 1, featured: 2, panelist: 3 };
            const tier = tierMap[data.show_tiers];
            if (tier) list = list.filter((s) => s.tier === tier);
          }
          setSpeakers(list.slice(0, data.max_speakers || 6));
        }
      } catch (e) {
        console.error('Failed to fetch speakers for preview', e);
      } finally {
        setLoading(false);
      }
    };
    fetchSpeakers();
  }, [data.max_speakers, data.show_tiers]);

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white" data-testid="section-speaker-preview">
        <div className="max-w-7xl mx-auto px-6 text-center">
          {data.title && <h2 className="text-3xl md:text-5xl font-bold text-[#0a1a2f] tracking-tight">{data.title}</h2>}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 md:gap-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden border border-slate-200/80 animate-pulse">
                <div className="aspect-[3/4] bg-slate-200" />
                <div className="p-4 space-y-2"><div className="h-4 bg-slate-200 rounded w-3/4" /><div className="h-3 bg-slate-100 rounded w-1/2" /></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }
  if (speakers.length === 0) return null;

  return (
    <section className="py-20 bg-gradient-to-b from-slate-50 to-white" data-testid="section-speaker-preview">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          {data.title && (
            <h2 className="text-3xl md:text-5xl font-bold text-[#0a1a2f] tracking-tight">
              <EditableText as="span" scope={scope('title')} value={data.title} multiline={false}>{data.title}</EditableText>
            </h2>
          )}
          {data.subtitle && (
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              <EditableText as="span" scope={scope('subtitle')} value={data.subtitle}>{data.subtitle}</EditableText>
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 md:gap-8">
          {speakers.map((speaker) => {
            const TierIcon = tierIcons[speaker.tier] || User;
            return (
              <div
                key={speaker.id}
                className="group relative bg-white rounded-2xl overflow-hidden border border-slate-200/80 shadow-sm hover:shadow-xl transition-all duration-300"
                data-testid={`speaker-preview-card-${speaker.id}`}
              >
                <div className="aspect-[3/4] bg-gradient-to-br from-[#00457f] to-[#003461] relative overflow-hidden">
                  {speaker.image_url ? (
                    <img
                      src={speaker.image_url}
                      alt={speaker.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-16 h-16 text-white/30" />
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <div className="flex items-center gap-1.5 mb-1">
                      <TierIcon className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider">
                        {tierLabels[speaker.tier] || 'Speaker'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-[#0a1a2f] text-sm md:text-base leading-tight">
                    {speaker.name}
                  </h3>
                  {speaker.title && (
                    <p className="text-xs md:text-sm text-slate-500 mt-1 line-clamp-2">
                      {speaker.title}
                    </p>
                  )}
                  {speaker.organization && (
                    <p className="text-xs text-slate-400 mt-0.5">{speaker.organization}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {data.cta && data.cta.label && (
          <div className="mt-12 text-center">
            <a href={data.cta.url || '/speakers'}>
              <Button className="bg-[#00457f] hover:bg-[#003461] text-white px-8 py-3 rounded-full text-base font-semibold gap-2">
                <EditableText as="span" scope={scope('cta.label')} value={data.cta.label} multiline={false}>{data.cta.label}</EditableText>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default SpeakerPreviewSection;
